import { Grid, Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog, TaskStatus, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { Button, StandardDialog } from '@roc/ui';
import { observer } from 'mobx-react';
import { isAccepted } from '../../../utils/documents';

interface Props {
  onClose: () => void;
  open: boolean;
  document: Document;
  loan: any;
}

const TITLE = 'Confirmation of Receipt - WS';

/**
 * Post closing confirms the wet-signed Note Allonge and Assignment and Conveyance originals arrived,
 * so there is no upload, only the confirmation. The TPO points wire is only sent once the loan is
 * funded and closed (closing date set): confirming after that sends it right away, confirming before
 * keeps it on hold until the loan closes.
 */
const WetSignReceiptConfirmationDialog = observer((props: Props) => {
  const { open, onClose, document, loan } = props;
  const { generalTasksStore } = useGeneralTasksStore();
  const taskId = document?.taskId;
  const confirmed = isAccepted(document);
  const loanClosed = !!loan?.closingDate;

  const handleConfirm = () => {
    generalTasksStore.updateTaskStatusV2(taskId, TaskStatus.ACCEPTED, onClose);
  };

  const dialogContent = (
    <Box py={2}>
      {confirmed ? (
        loanClosed ? (
          <Alert severity="success">
            Receipt of the wet-signed originals was confirmed and the TPO points wire was sent.
          </Alert>
        ) : (
          <Alert severity="success">
            Receipt of the wet-signed originals was confirmed. The TPO points wire is on hold until
            the loan is funded and closed, and will be sent then.
          </Alert>
        )
      ) : (
        <Alert severity="info">
          Confirm that both the <strong>Note Allonge</strong> and the <strong>Assignment and Conveyance</strong> originals arrived
          wet signed.{' '}
          {loanClosed
            ? 'The loan is funded and closed, so the TPO points wire is sent as soon as you confirm.'
            : 'The loan is not funded and closed yet, so the TPO points wire is not sent now: it stays on hold until the loan closes.'}
        </Alert>
      )}
    </Box >
  );

  const dialogActions = (
    <Grid container spacing={1} justifyContent="flex-end">
      <Grid item>
        <Button testId="cancel" variant="outlined" color="primary" onClick={onClose}>
          {confirmed ? 'Close' : 'Cancel'}
        </Button>
      </Grid>
      {!confirmed && (
        <Grid item>
          <Button
            testId="confirmWetSignReceipt"
            variant="contained"
            color="primary"
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </Grid>
      )}
    </Grid>
  );

  return taskId ? (
    <CustomTaskDialog
      title={TITLE}
      open={open}
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
      taskId={taskId}
    />
  ) : (
    <StandardDialog
      title={TITLE}
      open={open}
      maxWidth="sm"
      handleClose={onClose}
      dialogContent={dialogContent}
      dialogActions={dialogActions}
    />
  );
});

export default WetSignReceiptConfirmationDialog;
